package er.prateek.crossly.ads

/**
 * AdSense is a DOM product and Compose for Web paints to a single `<canvas>`, so an ad can't
 * be a composable the way it is on Android/iOS. Instead each banner slot owns a real `<ins>`
 * element in `document.body`, positioned over the canvas at the coordinates the composable
 * reports.
 *
 * `position: fixed` and viewport coordinates are used deliberately: the canvas fills the
 * viewport in this app's `index.html`, so viewport space and canvas space coincide and no
 * canvas-offset bookkeeping is needed.
 *
 * Kept as self-contained `js()` snippets rather than typed `kotlinx.browser` calls so this
 * file and its wasmJs twin can stay byte-identical below the package line -- the two targets
 * have no shared source set here, and Kotlin functions aren't callable from inside a `js()`
 * body anyway (name mangling), so each snippet has to stand alone.
 */

/** Idempotent: AdSense's loader must appear exactly once per page. */
internal fun ensureAdSenseScript(client: String): Unit = js(
    """{
        if (!document.getElementById('crossly-adsense-loader')) {
            var s = document.createElement('script');
            s.id = 'crossly-adsense-loader';
            s.async = true;
            s.crossOrigin = 'anonymous';
            s.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=' + client;
            document.head.appendChild(s);
        }
    }"""
)

/**
 * Creates (or leaves alone) the container for one placement. `data-adtest` is AdSense's
 * equivalent of AdMob's sample unit ids -- with it on, requests are served but never billed
 * or counted, which is what makes testing against a real publisher id safe.
 */
internal fun createAdSlot(domId: String, client: String, slot: String, testMode: Boolean): Unit = js(
    """{
        if (!document.getElementById('crossly-adsense-loader')) {
            var s = document.createElement('script');
            s.id = 'crossly-adsense-loader';
            s.async = true;
            s.crossOrigin = 'anonymous';
            s.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=' + client;
            document.head.appendChild(s);
        }
        if (!document.getElementById(domId)) {
            var host = document.createElement('div');
            host.id = domId;
            host.style.position = 'fixed';
            host.style.zIndex = '10';
            host.style.overflow = 'hidden';
            var ins = document.createElement('ins');
            ins.className = 'adsbygoogle';
            ins.style.display = 'inline-block';
            ins.style.width = '100%';
            ins.style.height = '100%';
            ins.setAttribute('data-ad-client', client);
            ins.setAttribute('data-ad-slot', slot);
            if (testMode) { ins.setAttribute('data-adtest', 'on'); }
            host.appendChild(ins);
            document.body.appendChild(host);
            (window.adsbygoogle = window.adsbygoogle || []).push({});
        }
    }"""
)

/** Called on every layout pass of the owning composable. */
internal fun positionAdSlot(domId: String, left: Double, top: Double, width: Double, height: Double): Unit = js(
    """{
        var host = document.getElementById(domId);
        if (host) {
            host.style.left = left + 'px';
            host.style.top = top + 'px';
            host.style.width = width + 'px';
            host.style.height = height + 'px';
        }
    }"""
)

internal fun removeAdSlot(domId: String): Unit = js(
    """{
        var host = document.getElementById(domId);
        if (host && host.parentNode) { host.parentNode.removeChild(host); }
    }"""
)
